import type { StyleValue } from 'vue'

/**
 * 检测对象是否包含 padding 相关属性
 * @param settings 要检测的对象
 * @returns 包含的 padding 属性信息
 */
export const hasPaddingProperties = (settings: any): {
  hasDesktopPadding: boolean
  hasMobilePadding: boolean
  hasAnyPadding: boolean
  desktopPaddingProps: string[]
  mobilePaddingProps: string[]
} => {
  const desktopPaddingProps = ['paddingTop', 'paddingBottom', 'paddingLeft', 'paddingRight']
  const mobilePaddingProps = ['mobile_paddingTop', 'mobile_paddingBottom', 'mobile_paddingLeft', 'mobile_paddingRight']

  const hasDesktopPadding = desktopPaddingProps.some(prop => settings?.[prop] !== undefined)
  const hasMobilePadding = mobilePaddingProps.some(prop => settings?.[prop] !== undefined)

  return {
    hasDesktopPadding,
    hasMobilePadding,
    hasAnyPadding: hasDesktopPadding || hasMobilePadding,
    desktopPaddingProps: desktopPaddingProps.filter(prop => settings?.[prop] !== undefined),
    mobilePaddingProps: mobilePaddingProps.filter(prop => settings?.[prop] !== undefined),
  }
}

/**
 * 生成组件的响应式样式
 * 优先使用移动端配置，其次使用桌面端配置
 * @param settings 组件设置对象
 * @returns 样式对象
 */
export const elementStyle = (settings: any, defaultValues?: {
  paddingTop?: number
  paddingBottom?: number
  paddingLeft?: number
  paddingRight?: number
}): Partial<StyleValue> => {
  // 如果没有传入 isMobile 参数，则自动检测
  const mobile = useIsMobile().value

  if (mobile) {
    return {
      paddingTop: `${settings?.mobile_paddingTop ?? settings?.paddingTop ?? defaultValues?.paddingTop ?? 0}px`,
      paddingBottom: `${settings?.mobile_paddingBottom ?? settings?.paddingBottom ?? defaultValues?.paddingBottom ?? 0}px`,
      paddingLeft: `${settings?.mobile_paddingLeft ?? settings?.paddingLeft ?? defaultValues?.paddingLeft ?? 0}px`,
      paddingRight: `${settings?.mobile_paddingRight ?? settings?.paddingRight ?? defaultValues?.paddingRight ?? 0}px`,
    }
  }

  return {
    paddingTop: `${settings?.paddingTop ?? defaultValues?.paddingTop ?? 0}px`,
    paddingBottom: `${settings?.paddingBottom ?? defaultValues?.paddingBottom ?? 0}px`,
    paddingLeft: `${settings?.paddingLeft ?? defaultValues?.paddingLeft ?? 0}px`,
    paddingRight: `${settings?.paddingRight ?? defaultValues?.paddingRight ?? 0}px`,
  }
}

/**
 * 生成组件的响应式 margin 样式
 * @param settings 组件设置对象
 * @param isMobile 是否为移动端
 * @returns 样式对象
 */
export const elementMarginStyle = (settings: any, isMobile?: boolean): Partial<StyleValue> => {
  const mobile = isMobile ?? useIsMobile().value

  if (mobile) {
    return {
      marginTop: `${settings?.mobile_marginTop ?? settings?.marginTop ?? 0}px`,
      marginBottom: `${settings?.mobile_marginBottom ?? settings?.marginBottom ?? 0}px`,
      marginLeft: `${settings?.mobile_marginLeft ?? settings?.marginLeft ?? 0}px`,
      marginRight: `${settings?.mobile_marginRight ?? settings?.marginRight ?? 0}px`,
    }
  }

  return {
    marginTop: `${settings?.marginTop ?? 0}px`,
    marginBottom: `${settings?.marginBottom ?? 0}px`,
    marginLeft: `${settings?.marginLeft ?? 0}px`,
    marginRight: `${settings?.marginRight ?? 0}px`,
  }
}

/**
 * 生成组件的完整响应式样式（包含 padding 和 margin）
 * @param settings 组件设置对象
 * @param isMobile 是否为移动端
 * @returns 样式对象
 */
export const elementFullStyle = (settings: any, isMobile?: boolean): Partial<StyleValue> => {
  const paddingStyle = elementStyle(settings, isMobile)
  const marginStyle = elementMarginStyle(settings, isMobile)

  return Object.assign({}, paddingStyle, marginStyle)
}
